/**
 * The Migrations table: status on this environment, the last run, and Dry run / Run per row.
 * A row click (or History) opens that migration's runs.
 */
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { MODE_LABELS, ticketUrl } from '../constants';
import type { Migration } from '../api/migrationsClient';
import { RunStateTag } from './MigrationRunsDrawer';
import { lastRunOf, runnerName } from '../format';
import { formatTime } from '../../envCopy/format';

const { Text } = Typography;

const stop = (e: React.MouseEvent) => e.stopPropagation();

export interface MigrationsTableProps {
  migrations: Migration[];
  loading: boolean;
  /** Every action is off: a run is in progress, a start is being sent, or the list is stale. */
  actionsOff: boolean;
  onDryRun: (m: Migration) => void;
  onRun: (m: Migration) => void;
  onOpen: (m: Migration) => void;
}

export const MigrationsTable: React.FC<MigrationsTableProps> = ({
  migrations,
  loading,
  actionsOff,
  onDryRun,
  onRun,
  onOpen,
}) => (
  <Table<Migration>
    rowKey="id"
    size="small"
    loading={loading}
    dataSource={migrations}
    pagination={false}
    locale={{ emptyText: loading ? ' ' : 'No migrations in the code on this environment.' }}
    onRow={(m) => ({ onClick: () => onOpen(m), style: { cursor: 'pointer' } })}
    columns={[
      {
        title: 'Migration',
        key: 'migration',
        render: (_, m) => (
          <Space direction="vertical" size={0}>
            {m.ticket && (
              <a href={ticketUrl(m.ticket)} target="_blank" rel="noreferrer" onClick={stop}>
                {m.ticket}
              </a>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {m.id}
            </Text>
          </Space>
        ),
      },
      { title: 'What it does', dataIndex: 'description', key: 'description' },
      {
        title: 'Status',
        key: 'status',
        render: (_, m) =>
          m.status === 'done' ? <Tag color="green">Done</Tag> : <Tag color="red">Not run</Tag>,
      },
      {
        title: 'Last run',
        key: 'last',
        render: (_, m) => {
          const run = lastRunOf(m);
          if (!run) return <Text type="secondary">Never</Text>;
          return (
            <Space direction="vertical" size={0}>
              <Space size={4} wrap>
                <Text>{MODE_LABELS[run.mode] ?? run.mode}</Text>
                <RunStateTag status={run.status} />
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {runnerName(run)}, {formatTime(run.startedAt)}
              </Text>
            </Space>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, m) => (
          <Space onClick={stop}>
            <Button size="small" disabled={actionsOff} onClick={() => onDryRun(m)}>
              Dry run
            </Button>
            <Tooltip
              title={
                m.canApply
                  ? undefined
                  : 'A dry run of this version must finish without errors first.'
              }
            >
              {/* The span keeps the tooltip working on a disabled button. */}
              <span>
                <Button
                  size="small"
                  type="primary"
                  danger
                  disabled={actionsOff || !m.canApply}
                  onClick={() => onRun(m)}
                >
                  Run
                </Button>
              </span>
            </Tooltip>
            <Button size="small" type="link" onClick={() => onOpen(m)}>
              History
            </Button>
          </Space>
        ),
      },
    ]}
  />
);
